import { Chip } from "@evidenceb/athena-common/design-system/Chip";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import { VisuallyHidden } from "@evidenceb/athena-common/ui/VisuallyHidden";
import { useFeatureFlag } from "@evidenceb/athena-common/modules/FeatureFlags";
import React, { useId } from "react";
import { defineMessages, useIntl } from "react-intl";
import { fullName } from "../../../../../../../utils/format";
import Avatar from "../../../../../../../components/Avatar/Avatar";
import {
    Classroom,
    Student,
    TeacherStudent,
    UserType,
} from "../../../../../../../interfaces/User";
import { StudentCollectionDetailsProps } from "../../../../../../../components/StudentCollectionDetails/StudentCollectionDetails";
import useTheme from "../../../../../../../hooks/useTheme";
import { useDashboardRouter } from "../../../../routing";
import cn from "classnames";
import { Link } from "react-router-dom";
import EditStudentForm from "../../../EditStudentForm/EditStudentForm";
import { useShowCodePrintingCheckboxes } from "../../../../../../../components/UsersCodePrinting/userCodePrintingHooks";
import UsersCodePrintingSelection from "../../../../../../../components/UsersCodePrinting/UserSelection/UsersSelection";
import { IGroup } from "../../../../Group/types";
import useOnlineStatus from "../../../../../../../hooks/useOnlineStatus";
import { commonMessages } from "../../../../../../../utils/messages";
import { useHasWorkmode } from "../../../../../../../hooks/useWorkmode";
import { WorkModeType } from "@evidenceb/athena-common/interfaces/Workmode";

type HeaderProps = {
    showCodes?: boolean;
};

const Header = ({ showCodes = false }: HeaderProps) => {
    const intl = useIntl();

    const showCodePrinting = useShowCodePrintingCheckboxes();

    return (
        <tr>
            <th scope="col">{intl.formatMessage(messages.name)}</th>
            {showCodes && (
                <>
                    <th scope="col">
                        {intl.formatMessage(messages.classroomCode)}
                    </th>
                    <th scope="col">
                        {intl.formatMessage(messages.studentCode)}
                    </th>
                </>
            )}
            <th scope="col">
                <div>
                    {showCodePrinting ? (
                        <UsersCodePrintingSelection.GlobalCheckbox className="user-list__student__checkbox user-list__student__checkbox__group" />
                    ) : (
                        <VisuallyHidden>
                            {intl.formatMessage(messages.studentDetails)}
                        </VisuallyHidden>
                    )}
                </div>
            </th>
        </tr>
    );
};

type RowProps = {
    classroom: Classroom | IGroup;
    student: Student | TeacherStudent;
    onClickEdit: () => void;
    onFinishEdit: () => void;
    studentToEdit: string;
    type: StudentCollectionDetailsProps["type"];
    index: number;
};
const Row = (props: RowProps) => {
    const { classroom, student, studentToEdit, type } = props;

    const intl = useIntl();
    const theme = useTheme();
    const { studentDetails: getStudentDetailsUrl } = useDashboardRouter();

    const studentManagementFlag = useFeatureFlag("studentManagement");
    const showCodePrinting = useShowCodePrintingCheckboxes();
    const hasSoloAIMode = useHasWorkmode(WorkModeType.SoloAI, UserType.Student);

    const name = fullName(student);

    return (
        <tr
            key={student.id}
            className={cn("user-list__table__row", {
                "user-list__table__row--editing": student.id === studentToEdit,
            })}
        >
            <RowHeader {...props} />

            {type === "classroom" && studentManagementFlag && (
                <>
                    <td>
                        <Chip
                            className="user-list__table__code"
                            input={(classroom as Classroom).code}
                            colorMode={theme}
                            size="small"
                        />
                    </td>

                    <td>
                        {"code" in student && student.code && (
                            <Chip
                                className="user-list__table__code"
                                input={(student as Student).code}
                                colorMode={theme}
                                size="small"
                            />
                        )}
                    </td>
                </>
            )}

            <td className="user-list__student-page-link">
                <div>
                    {showCodePrinting ? (
                        <UsersCodePrintingSelection.Checkbox
                            studentId={student.id}
                        />
                    ) : (
                        <>
                            {hasSoloAIMode && (
                                <Link
                                    className="user-list__student-page-link__btn"
                                    to={getStudentDetailsUrl(
                                        student.id,
                                        classroom.id
                                    )}
                                >
                                    <Icon
                                        path="chevron_right"
                                        size="medium"
                                        color="var(--text-main-default)"
                                    />
                                    <VisuallyHidden>
                                        {intl.formatMessage(
                                            commonMessages.goToStudentDetail,
                                            {
                                                student: name,
                                            }
                                        )}
                                    </VisuallyHidden>
                                </Link>
                            )}
                        </>
                    )}
                </div>
            </td>
        </tr>
    );
};

const RowHeader = ({
    type,
    student,
    studentToEdit,
    onFinishEdit,
    onClickEdit,
    classroom,
}: RowProps) => {
    const intl = useIntl();
    const isOnline = useOnlineStatus();
    const studentManagementFlag = useFeatureFlag("studentManagement");

    const headerId = useId();
    const name = fullName(student);

    return (
        <th scope="row" className="user-list__table__name" id={headerId}>
            {type === "classroom" && student.id === studentToEdit ? (
                <EditStudentForm
                    student={student as Student}
                    onClose={onFinishEdit}
                    classroomId={classroom.id}
                />
            ) : (
                <div>
                    <Avatar
                        name={name}
                        className="user-list__table__name__avatar"
                    />
                    {name}

                    {type === "classroom" && studentManagementFlag && (
                        <button
                            aria-labelledby={headerId}
                            className="user-list__table__name__edit"
                            onClick={onClickEdit}
                            disabled={!isOnline}
                        >
                            <Icon
                                path="edit"
                                style={{
                                    color: "var(--element-grey80)",
                                }}
                                size="medium"
                            />
                            <VisuallyHidden>
                                {intl.formatMessage(messages.renameStudent, {
                                    student: fullName(student),
                                })}
                            </VisuallyHidden>
                        </button>
                    )}
                </div>
            )}
        </th>
    );
};

const messages = defineMessages({
    name: {
        id: "user-list-name",
        defaultMessage: "Name",
    },
    classroomCode: {
        id: "user-list-classroomCode",
        defaultMessage: "Classroom code",
    },
    studentCode: {
        id: "user-list-studentCode",
        defaultMessage: "Student code",
    },
    studentDetails: {
        id: "user-list-studentDetails",
        defaultMessage: "Link to the student page",
    },
    renameStudent: {
        id: "user-list-renameStudent",
        defaultMessage: "Rename {student}",
    },
});

export default {
    Header,
    Row,
};
